import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Loader2, Save, Users, User, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PermissionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionItems, setPermissionItems] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [selectedType, setSelectedType] = useState('user');
  const [permissions, setPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [itemsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/permissions/items`, { headers }),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      
      setPermissionItems(itemsRes.data);
      setUsers(usersRes.data || []);
    } catch (e) {
      toast.error('Erro ao carregar dados de permissões');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissionsForTarget = async (target) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const [permRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/admin/permissions/${target}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/permissions/defaults`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const savedPerms = permRes.data.permissions || {};
      const defaults = defaultsRes.data || {};
      
      // Determine the role for defaults
      const roles = ['admin', 'general_manager', 'user', 'daily_leader'];
      let roleDefaults = {};
      if (roles.includes(target)) {
        roleDefaults = defaults[target] || {};
      } else {
        // Find user's role
        const userMatch = users.find(u => u.username === target);
        const role = userMatch?.role || 'user';
        roleDefaults = defaults[role] || defaults['user'] || {};
      }
      
      // Merge: use saved if category exists, otherwise use defaults
      const merged = {};
      Object.keys(permissionItems).forEach(category => {
        if (savedPerms[category] !== undefined) {
          merged[category] = savedPerms[category];
        } else {
          merged[category] = roleDefaults[category] || [];
        }
      });
      
      setPermissions(merged);
      setHasChanges(false);
    } catch (e) {
      toast.error('Erro ao carregar permissões');
    }
  };

  const handleTargetChange = (value) => {
    setSelectedTarget(value);
    // Determine if it's a role or user
    const roles = ['admin', 'general_manager', 'user', 'daily_leader'];
    if (roles.includes(value)) {
      setSelectedType('role');
    } else {
      setSelectedType('user');
    }
    loadPermissionsForTarget(value);
  };

  const togglePermission = (category, key) => {
    setPermissions(prev => {
      const current = prev[category] || [];
      const updated = current.includes(key)
        ? current.filter(k => k !== key)
        : [...current, key];
      return { ...prev, [category]: updated };
    });
    setHasChanges(true);
  };

  const toggleAllInCategory = (category, items, checked) => {
    setPermissions(prev => ({
      ...prev,
      [category]: checked ? items.map(i => i.key) : []
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedTarget) {
      toast.error('Selecione um usuário ou role');
      return;
    }
    try {
      setSaving(true);
      const token = sessionStorage.getItem('access_token');
      await axios.post(`${API}/admin/permissions`, {
        target: selectedTarget,
        target_type: selectedType,
        permissions
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Permissões salvas para ${selectedTarget}!`);
      setHasChanges(false);
    } catch (e) {
      toast.error('Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  const categoryLabels = {
    tabs: '📑 Abas Visíveis',
    home_cards: '🏠 Cards da Home',
    admin_subtabs: '⚙️ Sub-abas Admin',
    labor_elements: '📋 Elementos Labor',
    payroll_elements: '💰 Elementos Payroll',
    docs_elements: '📄 Elementos Docs',
    trucks_elements: '🚛 Elementos Trucks'
  };

  return (
    <div className="space-y-4" data-testid="permissions-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Shield className="h-5 w-5 text-purple-600" />
          Gerenciar Permissões
        </h2>
        {hasChanges && (
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar Permissões
          </Button>
        )}
      </div>

      {/* Target Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-1 block">Aplicar permissões para:</Label>
              <Select value={selectedTarget} onValueChange={handleTargetChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione um usuário ou role..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Roles */}
                  <SelectItem value="__separator_roles" disabled>
                    <span className="font-bold text-gray-500">— Por Role (aplica a todos) —</span>
                  </SelectItem>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2"><Users className="h-3 w-3" /> Todos ADMIN</span>
                  </SelectItem>
                  <SelectItem value="general_manager">
                    <span className="flex items-center gap-2"><Users className="h-3 w-3" /> Todos Gerentes (GM)</span>
                  </SelectItem>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2"><Users className="h-3 w-3" /> Todos PMs</span>
                  </SelectItem>
                  <SelectItem value="daily_leader">
                    <span className="flex items-center gap-2"><Users className="h-3 w-3" /> Todos Daily Leaders</span>
                  </SelectItem>
                  
                  {/* Individual Users */}
                  <SelectItem value="__separator_users" disabled>
                    <span className="font-bold text-gray-500">— Por Usuário Individual —</span>
                  </SelectItem>
                  {users.filter(u => u.is_active !== false).map(u => (
                    <SelectItem key={u.username} value={u.username}>
                      <span className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        {u.username} - {u.full_name || u.role}
                        <Badge variant="outline" className="text-xs ml-1">{u.role}</Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedTarget && (
              <Badge className="bg-purple-100 text-purple-800">
                {selectedType === 'role' ? 'Aplicando para ROLE' : 'Aplicando para USUÁRIO'}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permission Checkmarks */}
      {selectedTarget && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(permissionItems).map(([category, items]) => {
            const currentPerms = permissions[category] || [];
            const allChecked = items.length > 0 && items.every(i => currentPerms.includes(i.key));
            const someChecked = items.some(i => currentPerms.includes(i.key));
            
            return (
              <Card key={category}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{categoryLabels[category] || category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{currentPerms.length}/{items.length}</span>
                      <Checkbox
                        checked={allChecked}
                        onCheckedChange={(checked) => toggleAllInCategory(category, items, checked)}
                        className="data-[state=checked]:bg-purple-600"
                      />
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {items.map(item => (
                      <div key={item.key} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50">
                        <Checkbox
                          id={`${category}-${item.key}`}
                          checked={currentPerms.includes(item.key)}
                          onCheckedChange={() => togglePermission(category, item.key)}
                          className="data-[state=checked]:bg-blue-600"
                        />
                        <Label htmlFor={`${category}-${item.key}`} className="text-sm cursor-pointer flex-1">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* No target selected */}
      {!selectedTarget && (
        <div className="text-center py-12 text-gray-500">
          <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>Selecione um usuário ou role para gerenciar permissões</p>
        </div>
      )}
    </div>
  );
};

export default PermissionsTab;
